import type { ReactNode } from 'react';

/** Props for {@link SidebarProfile}. */
export interface SidebarProfileProps {
  /** The avatar, rendered by the app so each portal keeps its own. It is all that shows while the rail is closed. */
  avatar: ReactNode;
  /** The operator's display name. */
  name: string;
  /** An optional second line under the name, e.g. what clicking the card does. */
  detail?: ReactNode;
  /** An optional glyph at the far end — a chevron for navigation, a door for signing out. */
  trailing?: ReactNode;
  /** Whether the card stands for the page on screen. */
  active?: boolean;
  /** Whether the card is waiting on something, e.g. a sign-out in flight. */
  disabled?: boolean;
  onClick: () => void;
}

/**
 * The operator card at the bottom of the `PortalSidebar`: avatar, name and an optional trailing glyph in a
 * bordered card, which folds down to the bare avatar while the rail is closed.
 */
export function SidebarProfile({
  avatar,
  name,
  detail,
  trailing,
  active = false,
  disabled = false,
  onClick,
}: SidebarProfileProps) {
  return (
    <button
      type="button"
      className="pk-profile"
      onClick={onClick}
      disabled={disabled}
      aria-current={active ? 'page' : undefined}
    >
      <span className="pk-profile-avatar">{avatar}</span>
      <span className="pk-collapsible pk-profile-text">
        <span className="pk-profile-name">{name}</span>
        {detail && <span className="pk-profile-detail">{detail}</span>}
      </span>
      {trailing && <span className="pk-collapsible pk-profile-trailing">{trailing}</span>}
    </button>
  );
}
